/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import gql from 'graphql-tag';
import mbpLogger from 'mbp-logger';
import { Query } from 'react-apollo';
import {
    func, number, array, string, shape, bool, arrayOf,
} from 'prop-types';

import { GRAPHQL_ENV } from '../../../../../../../gql';
import AddonItemsBuilder from './AddonItemsBuilder';
import GraphqlPortalLoader from '../../../../../GraphqlCommonComponents/GraphqlPortalLoader';
import { addToCartDataType } from '../../../common/types';

function AddonDescBuilder({
    handleMiniCartModalClick,
    brand,
    maxAllowedAddons,
    product,
    addonsData,
    skuData,
    onCloseModal,
    brandId,
    partNumber,
    categoryId,
    subscription,
    subscriptionDetails,
    presentation_family,
    disableAddToCart,
    isAddToCartEnabled,
    categoryPath,
    categoryName,
    selectedSku,
    productSkus,
    updateWineStatus,
    deliveryData,
    isMobile,
    mobileAgeVerificationOpen,
    handleMobileAgeVerification,
    addToCartData,
}) {
    const addonsPartNumbers = [];
    addonsData.forEach((addonGroup) => {
        addonGroup.addons.forEach((addonitem) => {
            addonsPartNumbers.push(`"${addonitem.partNumber}"`);
        });
    });
    const partNumbers = `[${addonsPartNumbers.toString()}]`;
    const ADD_ONS_DESC_QUERY = gql`
    query PDPpageQuery {
        findProductByPartNumbers(brand: "${brand.domain}", environment: "${GRAPHQL_ENV}", partNumbers: ${partNumbers})
        {
            shortDescription, longDescription, partNumber
        }
       }
    `;

    return (
        <Query query={ADD_ONS_DESC_QUERY}>
            {({ loading, error, data }) => {
                if (loading) return <GraphqlPortalLoader />;
                if (error) {
                    mbpLogger.logError({
                        query: ADD_ONS_DESC_QUERY,
                        component: 'AddonDescBuilder.js',
                        message: 'Error loading data from Graphql',
                        env: GRAPHQL_ENV,
                        error,
                    });
                    return null;
                }
                if (!data) {
                    mbpLogger.logWarning({
                        query: ADD_ONS_DESC_QUERY,
                        component: 'AddonDescBuilder.js',
                        message: 'No data returned for query',
                        env: GRAPHQL_ENV,
                    });
                    return null;
                }

                if (!data?.findProductByPartNumbers) {
                    return null;
                }
                let addonData = addonsData;
                if (data?.findProductByPartNumbers?.length > 0) {
                    addonData = addonData.map((addonGroup) => {
                        const customAddonWithDesc = addonGroup;
                        customAddonWithDesc.addons = addonGroup?.addons?.map((addonitem) => {
                            const addon = addonitem;
                            const addonDescObj = data?.findProductByPartNumbers?.find((addonDesc) => addonDesc.partNumber === addonitem.partNumber);
                            addon.shortDescription = addonDescObj?.shortDescription || addonDescObj?.longDescription;
                            return addon;
                        });
                        return customAddonWithDesc;
                    });
                }
                return (
                    <AddonItemsBuilder
                        handleMiniCartModalClick={handleMiniCartModalClick}
                        brand={brand}
                        maxAllowedAddons={maxAllowedAddons}
                        product={product}
                        data={addonData}
                        skuData={skuData}
                        onCloseModal={onCloseModal}
                        brandId={brandId}
                        partNumber={partNumber}
                        categoryId={categoryId}
                        subscription={subscription}
                        subscriptionDetails={subscriptionDetails}
                        presentation_family={presentation_family}
                        disableAddToCart={disableAddToCart}
                        isAddToCartEnabled={isAddToCartEnabled}
                        categoryPath={categoryPath}
                        categoryName={categoryName}
                        selectedSku={selectedSku}
                        productSkus={productSkus}
                        updateWineStatus={updateWineStatus}
                        deliveryData={deliveryData}
                        isMobile={isMobile}
                        mobileAgeVerificationOpen={mobileAgeVerificationOpen}
                        handleMobileAgeVerification={handleMobileAgeVerification}
                        addToCartData={addToCartData}
                    />
                );
            }}
        </Query>
    );
}

AddonDescBuilder.propTypes = {
    handleMiniCartModalClick: func.isRequired,
    addonsData: arrayOf(shape({
        name: string.isRequired,
        maxAllowedAddons: number.isRequired,
        addons: arrayOf(shape({
            id: string.isRequired,
            shortDescription: string,
            name: string.isRequired,
        })),
    })).isRequired,
    brand: shape({
        domain: string,
    }).isRequired,
    brandId: string.isRequired,
    partNumber: string.isRequired,
    maxAllowedAddons: number.isRequired,
    onCloseModal: func,
    skuData: arrayOf(
        shape({
            id: string.isRequired,
            name: string.isRequired,
            prices: arrayOf(
                shape({
                    type: string.isRequired,
                    value: number.isRequired,
                }),
            ).isRequired,
        }),
    ).isRequired,
    selectedSku: shape({
        id: string.isRequired,
        minAge: string.isRequired,
    }).isRequired,
    product: shape({
        id: string.isRequired,
        name: string.isRequired,
        brandId: string.isRequired,
        partNumber: string.isRequired,
    }).isRequired,
    categoryId: string,
    subscriptionDetails: shape({
        subscriptionType: string,
        subscriptionDuration: string,
        subscriptionFrequency: string,
    }),
    subscription: shape({
        subscriptionKey: array,
        subscriptionIndefinite: string,
        subscriptionType: array,
        implementProductSubscription: bool,
        viewSubscription: bool,
        isTypeDiscovery: bool,
    }),
    presentation_family: string.isRequired,
    disableAddToCart: bool,
    isAddToCartEnabled: bool,
    categoryPath: string,
    categoryName: string,
    productSkus: arrayOf(
        shape({
            minAge: string.isRequired,
        }).isRequired,
    ).isRequired,
    deliveryData: shape({
        location_type_options: arrayOf(
            shape({
                name: string,
                value: string,
            }),
        ),
        show: bool,
        title: string,
        zip_code_title: string,
        location_type_title: string,
    }),
    updateWineStatus: func,
    isMobile: bool,
    mobileAgeVerificationOpen: bool,
    handleMobileAgeVerification: func,
    addToCartData: addToCartDataType,
};

AddonDescBuilder.defaultProps = {
    onCloseModal: () => {},
    isAddToCartEnabled: true,
    subscriptionDetails: {},
    disableAddToCart: false,
    subscription: {},
    updateWineStatus: () => {},
    deliveryData: {},
    categoryId: '',
    categoryPath: '',
    categoryName: '',
    isMobile: false,
    mobileAgeVerificationOpen: false,
    handleMobileAgeVerification: () => { },
    addToCartData: {},
};

export default AddonDescBuilder;
